//import EventStream from "Models/EventStream";
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import Phaser from 'phaser';
import { ErrorFeedbackManager } from 'Phaser/GameObjects/ErrorFeedbackManager';
import {
  CurrentSceneReady,
  ListenAddPhrase,
  ListenForReactEvent,
  StartedPlaying,
  ListenPulseNote,
  ListenChangeTempo,
  ListenSetNoteState,
  ListenStartExercise,
  NotifyReact,
  ReactToExerciseEventType,
  RemoveListener,
  ListenMidiOn,
  ListenMidiOff,
  ListenPauseExercise,
  ShowToast,
  ListenSetCursor,
  ListenResizeWindow,
  ExercisePaused,
  ListenSetFreePlayMode,
  ListenSetZoomLevel,
  ListenResetChevrons,
} from 'Phaser/EventBus';
import CursorMobile from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import {
  DefaultConfig,
  getPhraseEnd,
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
} from 'Phaser/GameObjects/StaffLine';
import { PrerenderedPhraseData } from 'Types';
import { INoteGraphicsDatabase } from './StaffExercise';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase, { Iterator } from 'Models/Phrase';
import TimeKeeper from 'Models/TimeKeeper';
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';
import ChevronManager from 'Phaser/Gameplay/ChevronManager';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import { getBraceWidth, optimizeStaffLineHeight } from 'Phaser/GameObjects/StaffLine/Util';
import { SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';

// limit the value to the nearest step (0.05)
function _limitValueToStep(value: number, step: number) {
  return Math.floor(value / step) * step;
}

class PlayablePhrase {
  cursor?: CursorMobile;
  graphic: PhraseGraphic;
  idx: number = 0;
  isActive: boolean = false;

  constructor(graphic: PhraseGraphic, cursor?: CursorMobile) {
    this.cursor = cursor;
    this.graphic = graphic;
  }

  activate(unpause?: boolean, opaque = true) {
    this.isActive = true;
    this.cursor?.enable(opaque);
    //this.cursor?.setAlpha();
    if (unpause) this.cursor?.unpause();
  }

  deactivate() {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause() {
    this.cursor?.pause();
  }

  update() {
    this.graphic.update();
    if (this.isActive) this.cursor?.update();
  }

  destroy() {
    this.graphic.destroy();
    this.cursor?.destroy();
  }
}

export default class SightReadingPhaser extends Phaser.Scene implements INoteGraphicsDatabase {
  phrases: (PlayablePhrase | undefined)[] = [];
  mainIterator!: IteratorExtensible;
  //cursors: CursorMobile[] = [];
  currentCursor: number = 0;
  currentIdx?: number;
  eventStream?: EventStreamPhaser2;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: TimeKeeper;

  phrasesSeen: Map<string, number> = new Map();

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  // This is a hack for maintaining the correct sequence when new phrases get loaded in
  // should no longer be necessary once we get all the data baked in from the backend
  phraseLoadQueue: {
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
    phraseRaw: Phrase;
  }[] = [];

  pauseQueued = false;
  playbackStarted = false;
  shouldUpdate = false;

  measureCount: number = 0;

  mobileView: boolean = false;
  width: number = 0;
  height: number = 0;
  private firstPhrase = true;

  topPhrase: number = 0;
  bottomPhrase: number = 1;

  private activePhrase = 0;
  zoomTween?: Phaser.Tweens.Tween;

  getActivePhrase(): number {
    return this.activePhrase;
  }
  private phrasesTillNextTier = 4;
  private freePlayMode: boolean = false;

  private gameplayStarted: boolean = false;

  private eventsQueue: [number, number][] = [];
  private startPlayingTriggerEventTimestamp?: number;

  private nextPhraseStartTimer?: Phaser.Time.TimerEvent;

  private chevronManager?: ChevronManager;
  private errorFeedbackManager?: ErrorFeedbackManager;
  private phrase1Gizmo?: Phaser.GameObjects.Rectangle;
  private phrase2Gizmo?: Phaser.GameObjects.Rectangle;

  private maxZoomLevel: number = 1;
  private zoomLevel: number = 1;

  constructor() {
    super('SightReadingPhaser');
  }

  create() {
    NotifyReact(CurrentSceneReady(this));
    this.events.once('shutdown', this.destroy, this);
  }

  debugLog(text: string) {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    console.debug(text);
  }

  init({
    eventStream,
    mobile,
    width,
    height,
    zoomLevel,
  }: {
    eventStream: EventStreamPhaser2;
    mobile?: boolean;
    width: number;
    height: number;
    zoomLevel: number;
  }) {
    console.debug('[zoom::init] loading scene with ', zoomLevel);
    if (zoomLevel) {
      this.zoomLevel = zoomLevel;
      this.cameras.main.zoom = Math.max(0.5, Math.min(zoomLevel, 1));
    } else {
      this.zoomLevel = 0.9;
    }
    //console.debug(this.cameras.main);
    //console.debug('[zoom::init] loading scene with ', zoomLevel, this.cameras.main.zoom);
    this.chevronManager = new ChevronManager(eventStream);
    this.errorFeedbackManager = new ErrorFeedbackManager(this);
    this.width = width;
    this.height = height;
    this.mobileView = mobile ?? false;
    this.timeKeeperAuxiliary = new TimeKeeper();
    //this.cameras.main.setZoom(0.5);
    this.eventStream = eventStream;
    // Set error feedback manager on event stream
    this.eventStream.setErrorFeedbackManager(this.errorFeedbackManager, this);
    this.phrases = [];
    this.cameras.main.setBackgroundColor('0xebebeb');
    //this.log = this.add.text(0, 0, "log");
    ListenForReactEvent(ListenResizeWindow(this.resize), this);
    ListenForReactEvent(ListenPauseExercise(this.pauseExercise), this);
    ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenMidiOn(this.noteOn), this);
    ListenForReactEvent(ListenMidiOff(this.noteOff), this);
    ListenForReactEvent(ListenSetZoomLevel(this.setZoomLevel), this);
    ListenForReactEvent(ListenSetNoteState(this.setNoteState), this, false, false);
    ListenForReactEvent(ListenPulseNote(this.pulseNote), this, false, false);
    ListenForReactEvent(ListenSetNoteState(this.setNoteState), this, false, false);
    ListenForReactEvent(ListenStartExercise(this.startExercise), this, false, false);
    ListenForReactEvent(ListenChangeTempo(this.changeTempo), this, false, false);
    //ListenForReactEvent(ListenAddPhrase(this.addPhrase), this, false, true);
    ListenForReactEvent(ListenSetCursor(this.setCursor), this);
    ListenForReactEvent(ListenSetFreePlayMode(this.setFreePlayMode), this);
    ListenForReactEvent(
      ListenResetChevrons(() => {
        this.chevronManager?.reset();
      }),
      this,
    );
    this.clickAudio = this.sound.add('click');
    /*this.phrase1Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase2Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase1Gizmo?.setOrigin(0, 0);
    this.phrase2Gizmo?.setOrigin(0, 0);*/
  }

  updateMaxZoomLevel(paddingHorizontal: number, paddingVertical: number, braceWidth: number) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- Phaser Scene is a class; feature flag read at runtime
    if (!useFeatureFlag(FeatureFlag.USE_LESSON_ZOOM)) return;
    const cam = this.cameras.main;
    const vpWidth = cam.width;
    const vpHeight = cam.height;
    const phraseWidth =
      vpWidth - paddingHorizontal * 2 + braceWidth * 2 + 5 * window.devicePixelRatio;
    const phraseHeight = vpHeight - paddingVertical * 2 + 5 * window.devicePixelRatio;
    const maxZoomLevel = Math.min(vpWidth / phraseWidth, vpHeight / phraseHeight);
    this.maxZoomLevel = maxZoomLevel;
    //console.debug('[zoom::updateMaxZoomLevel]', cam.zoom, zoomLevel);
    this.setZoomLevel({ zoomLevel: this.zoomLevel });
    console.debug('braceWidth', braceWidth);
    console.debug('[zoom::updateMaxZoomLevel] updating max zoom level', maxZoomLevel);
    console.debug('camera zoom after updateMaxZoomLevel', this.cameras.main.zoom);
  }

  setZoomLevel({ zoomLevel }: { zoomLevel: number }) {
    console.debug('[zoom::setZoomLevel] setting zoom level', zoomLevel);
    if (zoomLevel === undefined) return;
    //console.debug('[zoom::setZoomLevel] setting zoom level', zoomLevel);
    // eslint-disable-next-line react-hooks/rules-of-hooks -- Phaser Scene is a class; feature flag read at runtime
    if (!useFeatureFlag(FeatureFlag.USE_LESSON_ZOOM)) return;
    const zoom = Math.max(0.5, Math.min(zoomLevel, 1)) * this.maxZoomLevel;
    this.zoomTween?.stop();
    this.zoomTween = this.tweens.add({
      targets: this.cameras.main,
      zoom,
      duration: 500,
      ease: 'Power2',
      onComplete: () => {
        console.debug('camera zoom after setZoomLevel complete', this.cameras.main.zoom);
      },
    });
    console.debug('camera zoom after setZoomLevel', this.cameras.main.zoom);
  }

  setFreePlayMode({ enabled }: { enabled: boolean }) {
    this.freePlayMode = enabled;
  }

  startExerciseDummy() {
    NotifyReact(StartedPlaying());
  }

  setCursor(_props: { measure?: number }) {
    if (!this.playbackStarted) return;
    const measure = Math.max(
      this.phrases[this.activePhrase]!.cursor!.getLastPlayedMeasure() - 1,
      0,
    );
    if (measure === -1) return;
    const phraseIdx = this.phrases.findIndex((phrase) => phrase?.graphic.containsMeasure(measure));
    if (phraseIdx === undefined) {
      console.error("given measure doesn't appear in any of the active phrases!");
    } else {
      const phrase = this.phrases[phraseIdx]!;
      if (phrase !== this.phrases[this.activePhrase]) {
        const current = this.phrases[this.activePhrase]!;
        const firstNotePos = this.phrases[this.activePhrase]!.graphic.getFirstNotePosition();
        //this.phrases[this.activePhrase]?.cursor?.
        current.cursor?.setStart(firstNotePos, current.graphic._data.startTimestamp, 0);
        //this.phrases[this.activePhrase]?.cursor?.setLastNotePos(firstNotePos);
        current.cursor?.setX(firstNotePos - current.cursor!.width * 2);
        current?.deactivate();
        this.activePhrase = phraseIdx;
      }
      phrase.activate();
      const firstNote = phrase.graphic.getFirstNotePositionInMeasure(
        measure - phrase.graphic.phraseStartingMeasure!,
      );
      const cursorStartPos = firstNote!;
      //let cursor = this.cursors[this.currentCursor];
      const numMeasuresIn = measure - phrase.graphic.phraseStartingMeasure!;
      const timesig = phrase.graphic._data.timeSignature;
      /*if (cursor.startingMeasure > measure) {
        const phrase = this.phrases[this.currentIdx!]!;
        const timestamp = phrase.graphic._data.startTimestamp;
        const firstNote = phrase.graphic.getFirstNotePosition();
        cursor.setAlpha(0);
        cursor.setStart(firstNote, timestamp, 0);
        this.cursors[this.currentCursor].setX(
          this.phrases[this.currentIdx!]?.graphic.getPhraseStartX()!
        );
        this.currentCursor = (this.currentCursor + 1) % 2;
        cursor = this.cursors[this.currentCursor];
      }*/
      //cursor.clearBlink();
      const cursor = this.phrases[this.activePhrase]!.cursor!;
      cursor.lastPlayedMeasure = measure - cursor.startingMeasure;
      cursor.setStart(
        cursorStartPos,
        (timesig.numerator / timesig.denominator) * numMeasuresIn,
        measure - cursor.startingMeasure,
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }) {
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);
  }

  metronomeClick() {
    if (this.clickAudio === undefined) {
      Logger.warn('metronome audio not loaded!');
    }
    this.clickAudio?.stop();
    this.clickAudio?.play();
  }

  startExercise({ tempo }: { tempo?: number }) {
    if (tempo) {
      this.tempo = tempo;
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      //Tone.start();
      //this.metronomeClick();
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      Tone.start();
      //NotifyReact(StartedPlaying());
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(this.tempo, 0, measureOffset, NoteType._32nd);
      this.phrases[0]!.activate();
      //this.phrases[0]!.cursor!.setAlpha(1);
      this.phrases[0]!.cursor!.blink((60 / this.tempo) * 1000);
      //this.cursors[this.currentCursor].blink((60 / this.tempo) * 1000);
      //NotifyReact(StartedPlaying());
      //this.playbackStarted = true;
      //this.cursors[this.currentCursor].unpause();
    } else {
      NotifyReact(StartedPlaying());
      this.phrases[this.activePhrase]?.activate();
      this.phrases[this.activePhrase]?.cursor?.unpause();
    }
  }

  // runs every 'tick'
  tickUpdate(beatDuration: number) {
    if (this.playbackStarted) {
      if (!this.freePlayMode) this.chevronManager?.onTick();
    } else {
      this.metronomeClick();
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
  }

  update() {
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    if (this.shouldUpdate) {
      this.phrases.forEach((p) => p?.update());
      const timeKeeper = this.playbackStarted
        ? this.eventStream?.timeKeeper
        : this.timeKeeperAuxiliary;
      if (timeKeeper === undefined) {
        Logger.warn('timekeeper unavailable!');
        return;
      }
      const timestamp = timeKeeper.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (measureDurationSinceLastTickTime > beatFraction) {
        const beatDuration = (60 / (this.tempo ?? 72)) * 1000;
        this.tickUpdate(beatDuration);
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  showToast(message: string) {
    console.debug(message);
  }

  onPlayingLastSlice(start: number, duration: number, _durationInSecs: number) {
    if (!this.mobileView) {
      const nextPhrase = (this.activePhrase + 1) % 4;
      const phrase = this.phrases[nextPhrase];
      console.log(
        `[onPlayingLastSlice] activePhrase=${this.activePhrase} nextPhrase=${nextPhrase} exists=${!!phrase} destroyed=${phrase?.graphic.isDestroyed}`,
      );
      if (phrase) {
        const firstNotePos = phrase.graphic.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0));
        phrase.activate(false, false);
        phrase.cursor?.startCountingIn(start, duration, duration);
      }
    }
  }

  onFinishPlayingPhrase(_eligible?: boolean) {
    const eventStream = this.eventStream;
    const acc = eventStream != null ? eventStream.calcAccuracy() : 0;
    if (!this.freePlayMode) {
      this.chevronManager?.onPhraseFinishPlaying();
    }
    if (!this.mobileView) {
      console.log(
        `[onFinishPlayingPhrase] deactivating slot ${this.activePhrase}, activating slot ${(this.activePhrase + 1) % 4}`,
      );
      this.phrases[this.activePhrase]?.deactivate();
      this.activePhrase = (this.activePhrase + 1) % 4;
      this.phrases[this.activePhrase]?.activate(true);
      this.phrases[this.activePhrase]?.cursor?.setEligible(acc >= 95);
    }
  }

  addPhrase(phraseData: {
    phraseRaw: Phrase;
    phrase: PhraseData;
    cursorData: PrerenderedPhraseData[];
  }) {
    let phraseToLoad = phraseData;
    // Do not add a phrase if its starting timestamp is found to be earlier than the phrase that is
    // currently being played because it is probably a duplicate addPhrase call.
    if (
      this.phrases[this.activePhrase] &&
      phraseToLoad.phrase.startTimestamp <= this.phrases[this.activePhrase]!.cursor!.getTimestamp()
    ) {
      return;
    }
    // Check if we have pending phrases in the load queue that need to be loaded BEFORE the phrase that
    // is currently being requested to load (the load queue is assumed to always be sorted)
    this.phraseLoadQueue = this.phraseLoadQueue.sort(
      (a, b) => b.phrase.startTimestamp - a.phrase.startTimestamp,
    );
    const inQueue = this.phraseLoadQueue.at(0);
    if (inQueue && inQueue.phrase.startTimestamp < phraseToLoad.phrase.startTimestamp) {
      phraseToLoad = inQueue;
      this.phraseLoadQueue[0] = phraseData;
    }
    const { phrase, phraseRaw, cursorData } = phraseToLoad;
    if (phraseToLoad && this.phrasesSeen.get(phrase.uuid) !== phrase.startTimestamp) {
      console.log(
        `[addPhrase] Adding phrase uuid=${phrase.uuid} startTimestamp=${phrase.startTimestamp} currentIdx=${this.currentIdx} activePhrase=${this.activePhrase} phrases.length=${this.phrases.length}`,
      );
      console.log(
        `[addPhrase] Current phrase slots:`,
        this.phrases.map((p, i) =>
          p
            ? `[${i}] uuid=${p.graphic._data.uuid} ts=${p.graphic._data.startTimestamp} active=${p.isActive} destroyed=${p.graphic.isDestroyed}`
            : `[${i}] empty`,
        ),
      );
      if (phrase.startTimestamp !== 0 && this.phrases.length === 0) {
        console.log(`[addPhrase] Queuing phrase (no phrases loaded yet, startTimestamp != 0)`);
        this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
        return;
      }
      if (this.currentIdx) {
        const nextPhrase = this.phrases[(this.currentIdx + 3) % 4]?.graphic._data;
        if (this.phrases[this.activePhrase]) {
          const firstPhrasePlaying =
            this.phrases.length == 2 &&
            this.phrases[this.activePhrase]!.cursor!.getTimestamp() <
              getPhraseEnd(this.phrases[0]!.graphic._data);
          if (
            firstPhrasePlaying ||
            (nextPhrase &&
              this.phrases[this.activePhrase]!.cursor!.getTimestamp() < getPhraseEnd(nextPhrase))
          ) {
            console.log(`[addPhrase] Queuing phrase (active phrase still playing)`);
            this.phraseLoadQueue.push({ phrase, cursorData, phraseRaw });
            return;
          } else if (this.phrases[this.activePhrase] && nextPhrase) {
            // intentionally empty
          }
        }
      }
      const x = this.mobileView
        ? this.currentIdx !== undefined
          ? this.phrases[this.currentIdx]!.graphic.getNextPosition()
          : 100
        : 100;
      const _idx = this.currentIdx ?? 0;
      if (_idx % 2 === 0) {
        this.topPhrase = _idx;
      } else {
        this.bottomPhrase = _idx;
      }
      this.currentIdx = this.currentIdx !== undefined ? (this.currentIdx + 1) % 4 : 0;
      phrase.showClef = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showBrace = !this.mobileView || phrase.startTimestamp === 0;
      phrase.showTimeSig = phrase.startTimestamp === 0;
      const phraseGraphic = new PhraseGraphic(
        this,
        x,
        {
          globalScaling: 1,
          pitchLineGap: 20,
          pitchLineWidth: 1 * window.devicePixelRatio,
          measureLineWidth: 2 * window.devicePixelRatio,
          clefGap: 100,
          doubleEndLines: false,
        },
        phrase,
        'None',
        this.measureCount,
        undefined,
        this.currentIdx % 2 === 1,
      );
      this.measureCount += phrase.measureData.length; // Hack/Jank: This is hardcoded for now, maybe look into making it dynamic
      const idx = this.currentIdx;
      const outgoingSlot = (idx + 2) % 4;
      const outgoingPhrase = this.phrases[outgoingSlot];
      console.log(
        `[addPhrase] New phrase placed at slot ${idx}, outgoingSlot=${outgoingSlot}, outgoing exists=${!!outgoingPhrase}, bottom=${this.currentIdx % 2 === 1}`,
      );
      outgoingPhrase?.graphic.transitionOut(() => {
        console.log(
          `[transitionOut callback] outgoingSlot=${outgoingSlot}, still same phrase=${this.phrases[outgoingSlot] === outgoingPhrase}, fading in slot=${idx ?? 0}`,
        );
        if (this.phrases[outgoingSlot] === outgoingPhrase) {
          this.phrases[outgoingSlot]?.destroy();
          this.phrases[outgoingSlot] = undefined;
        }
        this.phrases[idx ?? 0]?.graphic.transition('Fade');
      }, this);
      const cameraHeight = this.cameras.main.height;
      const phraseGap = 50 * window.devicePixelRatio;
      const availVertSpace = phraseGraphic.getBoundingBoxHeight() * 2 + phraseGap;
      const paddingVert = (cameraHeight - availVertSpace) / 2;
      const phraseY = !this.mobileView
        ? paddingVert +
          (this.currentIdx % 2 === 1 ? phraseGraphic.getBoundingBoxHeight() + phraseGap : 0)
        : this.cameras.main.height / 2 - phraseGraphic.getBoundingBoxHeight() / 2;
      phraseGraphic.setPosition(x, phraseY);
      const cursorHeight = phraseGraphic.getBoundingBoxHeight() * 1.35;
      const cursorWidth = cursorHeight / 10;
      const cursorY = phraseGraphic.getBoundingBoxHeight() / 2 - cursorHeight / 2 + phraseGraphic.y;
      const firstNotePosition = phraseGraphic.getFirstNotePosition(0);
      const phraseCursor = new CursorMobile(
        this,
        firstNotePosition,
        cursorY,
        cursorWidth,
        cursorHeight,
        this.eventStream!,
        new IteratorExtensible(this, [], phraseRaw, phrase.timeSignature),
        phraseData.cursorData,
        this.mobileView,
        this.onPlayingLastSlice,
        this.onFinishPlayingPhrase,
        this,
        this.phrasesSeen.size > 0,
        phrase.startTimestamp,
        this.currentIdx,
        this.phrases.length === 0,
      );
      phraseCursor.reset(
        new IteratorExtensible(this, [], phraseRaw, phrase.timeSignature),
        phrase.startTimestamp,
        phraseGraphic.phraseStartingMeasure,
      );
      if (this.phrases.length === 0) {
        console.log(`[addPhrase] First phrase, placing at slot 0`);
        this.phrases[0] = new PlayablePhrase(phraseGraphic, phraseCursor);
      } else {
        const idx = this.currentIdx;
        if (this.phrases.length >= 4 && this.currentIdx !== undefined) {
          const destroyedPhrase = this.phrases[idx];
          if (destroyedPhrase) {
            console.log(
              `[addPhrase] Destroying existing phrase at slot ${idx}: uuid=${destroyedPhrase.graphic._data.uuid} isDestroyed=${destroyedPhrase.graphic.isDestroyed}`,
            );
            this.errorFeedbackManager?.cleanupPhrase(destroyedPhrase.graphic._data.uuid);
          }
          this.phrases[idx]?.destroy();
        }
        console.log(`[addPhrase] Placing new phrase at slot ${this.currentIdx}`);
        this.phrases[this.currentIdx] = new PlayablePhrase(phraseGraphic, phraseCursor);
      }
      if (!this.mobileView) this.resize({ width: this.width, height: this.height });
      phraseCursor.addPhrase(cursorData, phraseRaw.uuid, phrase.timeSignature);
      this.eventStream?.addEvents2(
        new Iterator(cursorData, phraseRaw),
        phraseRaw.getStartTimestamp(),
      );
      this.phrasesSeen.set(phrase.uuid, phrase.startTimestamp);
      this.firstPhrase = false;
    } else {
      // intentionally empty
    }
  }

  getNoteById(id: string, phraseStart?: number): IPlayable | null {
    const note = this.phrases.map((phrase) => ({
      note: phrase?.graphic.getNoteById(id),
      phraseStart,
    }));
    note.forEach((n) => {
      if (n.note !== undefined && n.phraseStart !== phraseStart) {
        // intentionally empty
      }
    });
    return note.map((n) => n.note).find((n) => n !== undefined) ?? null;
  }

  // The update function might still be running when the Puase callback is triggered by
  // the registered event which will mess up the validity of the state.
  // Therefore, we queue a pause request which then immediately gets processed
  // in the next update() tick.
  pauseExercise() {
    this.pauseQueued = true;
  }

  _pauseExercise() {
    this.shouldUpdate = false;
    this.lastTickTime = undefined;
    this.phrases[this.activePhrase]?.pause();
    const lastPlayedMeasure = Math.max(
      this.phrases[this.activePhrase]!.cursor!.getLastPlayedMeasure() - 1,
      0,
    );
    NotifyReact(ExercisePaused(lastPlayedMeasure));
    this.pauseQueued = false;
    this.setCursor({});
  }

  getCurrentPhrase() {
    return this.phrases.find((phrase) => phrase?.isActive)?.graphic ?? null;
  }

  setNoteState({
    id,
    state,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    const note = this.getNoteByIdAndTimestamp(id, noteTimestamp);
    if (note) {
      console.log(
        `[setNoteState] id=${id} state=${state} noteTimestamp=${noteTimestamp} found in phrase`,
      );
    } else {
      console.warn(
        `[setNoteState] id=${id} state=${state} noteTimestamp=${noteTimestamp} NOT FOUND in any phrase`,
      );
    }
    note?.setTimingState(state, colorblindType);
  }

  pulseNote({
    id,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    this.getNoteByIdAndTimestamp(id, noteTimestamp)?.pulse(colorblindType);
  }

  getNoteByIdAndTimestamp(id: string, timestamp?: number): IPlayable | null {
    for (let i = 0; i < this.phrases.length; i++) {
      const phrase = this.phrases[i];
      if (!phrase) continue;
      const note = phrase.graphic.getNoteById(id, timestamp);
      if (note != null) {
        console.log(
          `[getNoteByIdAndTimestamp] Found note id=${id} ts=${timestamp} in slot ${i} (phrase uuid=${phrase.graphic._data.uuid} startTs=${phrase.graphic._data.startTimestamp} destroyed=${phrase.graphic.isDestroyed})`,
        );
        return note as IPlayable;
      }
    }
    return null;
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean) {
    const x =
      this.phrases
        .map((p) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((x) => x !== undefined) ?? null;
    return x;
  }

  getPhraseEndPosition() {
    return Math.max(
      ...this.phrases
        .filter((p) => p !== undefined)
        .map((phrase) => phrase.graphic.getNextPosition()),
    );
  }

  setNoteColor(color: string | number, id?: string, _colorblindType?: COLORBLIND_TYPE | null) {
    if (id === undefined)
      // target first note in current phrase
      id = this.getCurrentPhrase()?.getFirstNoteId();
    if (id === undefined) return;
    this.getCurrentPhrase()?.getNoteById(id)?.setColor(color);
  }

  /**
   * Get the current cursor position and active phrase.
   * Used by error feedback system to spawn visuals at cursor location.
   */
  getCursorPosition(): { x: number; phrase: PlayablePhrase } | null {
    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase || !activePhrase.cursor) {
      return null;
    }
    return {
      x: activePhrase.cursor.x,
      phrase: activePhrase,
    };
  }

  startPlaying() {
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();
    this.phrases[0]?.activate();
    this.phrases[0]?.cursor?.unpause(0);
    NotifyReact(StartedPlaying());
    this.playbackStarted = true;
  }

  noteOn({ note, velocity }: { note: number; velocity: number }) {
    if (this.playbackStarted && !this.gameplayStarted) {
      this.eventsQueue.push([note, velocity]);
    }
    if (!this.playbackStarted && this.eventStream !== undefined) {
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) return;
      const timeDiff = timestamp - this.lastTickTime;
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        //this.showToast(`Too Late!`);
        NotifyReact(ShowToast('Too late!'));
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        //this.showToast(`Too early!`);
        NotifyReact(ShowToast('Too early!'));
      } else {
        this.timeKeeperAuxiliary?.pause();
        this.timeKeeperAuxiliary?.resetTime();
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.phrases[0]?.activate();
        this.phrases[0]?.cursor?.unpause(measureOffset);
        this.eventsQueue.push([note, velocity]);
        NotifyReact(
          StartedPlaying({
            measureOffset,
            processEventsQueue: [this.processEventsQueue, this],
          }),
        );
        this.playbackStarted = true;
      }
    }
  }

  processEventsQueue() {
    this.eventsQueue.forEach(([note, velocity]) => {
      if (velocity === 0) this.eventStream?.handleNoteOffEvent(note);
      else this.eventStream?.handleNoteOnEvent(note, velocity);
    });
    this.eventsQueue = [];
  }

  noteOff({ note: _note }: { note: number }) {}

  destroy() {
    // Clean up error feedback for all phrases
    this.errorFeedbackManager?.cleanupAll();
    this.phrases.forEach((p) => p?.destroy());
    this.phrases.clear();
    RemoveListener(ReactToExerciseEventType.SetCursor);
    RemoveListener(ReactToExerciseEventType.AddPhrase);
    RemoveListener(ReactToExerciseEventType.ResizeWindow);
    RemoveListener(ReactToExerciseEventType.SetFreePlayMode);
    RemoveListener(ReactToExerciseEventType.SetZoomLevel);
    RemoveListener(ReactToExerciseEventType.PauseExercise);
    RemoveListener(ReactToExerciseEventType.StartExercise);
    RemoveListener(ReactToExerciseEventType.ChangeTempo);
    RemoveListener(ReactToExerciseEventType.SetNoteState);
    RemoveListener(ReactToExerciseEventType.PulseNote);
    RemoveListener(ReactToExerciseEventType.SetCursorVisible);
    RemoveListener(ReactToExerciseEventType.UnloadExercise);
    RemoveListener(ReactToExerciseEventType.MidiOn);
    RemoveListener(ReactToExerciseEventType.MidiOff);
    RemoveListener(ReactToExerciseEventType.SetFreePlayMode);
    RemoveListener(ReactToExerciseEventType.ResetChevrons);
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }

  resize(props: { width: number; height: number }) {
    const width = props.width;
    const height = props.height;
    this.width = width;
    this.height = height;
    this.scale.resize(width * window.devicePixelRatio, height * window.devicePixelRatio);
    const hasAnyPhrase = this.phrases.some((p) => p !== undefined);
    if (!hasAnyPhrase) {
      console.log(`[SightReadingPhaser::resize] BAILED — no phrases loaded`);
      return;
    }
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    const maxProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 50,
      idealPhraseConfig: DefaultConfig({}),
      minPhraseConfig: MediumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 150,
      idealPaddingY: 75,
    };
    const minProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 30,
      idealPhraseConfig: maxProps.minPhraseConfig,
      minPhraseConfig: MinimumConfig(),
      idealPaddingX: maxProps.minPaddingX,
      idealPaddingY: maxProps.minPaddingY,
      minStaffSpacingVertical: 20,
    };
    const { paddingX, paddingY, phraseRenderingConfig, staffSpacingVertical, phraseDimens, fit } =
      optimizeStaffLineHeight(
        [width, height],
        { min: minProps, max: maxProps },
        minAspectRatio,
        maxAspectRatio,
      );
    if (!fit)
      console.warn(
        '[SightReadingPhaser] not enough space for staff lines! Falling back to minimum configuration',
      );
    const { topClefNumExtraLinesTop, innerHeight } = phraseDimens;
    const minWidth = minAspectRatio * innerHeight;
    const availableWidth = Math.max(minWidth, width - paddingX * 2);
    const braceWidth = getBraceWidth(phraseDimens, this);
    this.updateMaxZoomLevel(
      paddingX * window.devicePixelRatio,
      paddingY * window.devicePixelRatio,
      braceWidth,
    );
    console.log(
      `[SightReadingPhaser::resize] Resizing ${this.phrases.filter((p) => p !== undefined).length} phrases. Slots:`,
      this.phrases.map((p, i) =>
        p
          ? `[${i}] uuid=${p.graphic._data.uuid} bottom=${p.graphic.bottom} destroyed=${p.graphic.isDestroyed}`
          : `[${i}] empty`,
      ),
    );
    this.phrases.forEach((p, slotIdx) => {
      if (p === undefined) return;
      const phraseHeightTotal =
        phraseDimens.topClefHeight.outerHeight +
        phraseDimens.bottomClefHeight.outerHeight +
        phraseRenderingConfig.clefGap +
        1 * phraseRenderingConfig.pitchLineGap;
      const yOffset = topClefNumExtraLinesTop * 0.5 * phraseRenderingConfig.pitchLineGap;
      const phraseY =
        paddingY + yOffset + (p.graphic.bottom ? phraseHeightTotal + staffSpacingVertical : 0);
      console.log(
        `[SightReadingPhaser::resize] slot ${slotIdx}: bottom=${p.graphic.bottom} phraseY=${phraseY} availableWidth=${availableWidth}`,
      );
      p.graphic.resize(
        phraseRenderingConfig,
        availableWidth,
        paddingX,
        phraseY,
        phraseDimens,
        innerHeight,
      );
      const c = p?.cursor;
      if (c === undefined) return;
      c.height = innerHeight * 1.25 * window.devicePixelRatio;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      const y = (phraseY + innerHeight / 2) * window.devicePixelRatio - c.height / 2;
      c.setY(y);
      if (!this.playbackStarted && c.idx === 0) {
        const firstNotePhrase =
          this.phrases[this.activePhrase] ?? this.phrases.find((ph) => ph !== undefined);
        const firstNote = firstNotePhrase?.graphic.getFirstNotePosition();
        if (firstNote !== undefined) {
          c.setLastNotePos(firstNote - c.width / 2);
          c.setX(firstNote - c.width / 2);
        }
      }
      if (p.graphic.bottom) {
        this.phrase2Gizmo?.setSize(
          availableWidth * window.devicePixelRatio,
          phraseHeightTotal * window.devicePixelRatio,
        );
        this.phrase2Gizmo?.setX(paddingX * window.devicePixelRatio);
        this.phrase2Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
      } else {
        this.phrase1Gizmo?.setY((phraseY - yOffset) * window.devicePixelRatio);
        this.phrase1Gizmo?.setX(paddingX * window.devicePixelRatio);
        this.phrase1Gizmo?.setSize(
          availableWidth * window.devicePixelRatio,
          phraseHeightTotal * window.devicePixelRatio,
        );
      }
    });
  }
}
